import {
  useSimulateContract,
  useWriteContract,
  useWaitForTransactionReceipt,
  useAccount,
} from 'wagmi';
import { encodeFunctionData } from 'viem';
import { useEffect } from 'react';
import BigNumber from 'bignumber.js';
import { notifications } from '@mantine/notifications';
import StakingABI from 'src/api/smart-contracts/abi/Staking';
import { CONTRACT_ADDRESSES } from 'src/constants';
import { formatDecimal, trackSCWriteEvent } from 'src/utils';
import { useEstimateGas } from 'src/hooks/useEstimateGas';
import { TransactionLink } from 'src/components/TransactionLink/TransactionLink';
import { useRedeemedAmount } from './useRedeemedAmount';

export function useRedeem({
  requestIds,
  onSuccess,
}: {
  requestIds: string[];
  onSuccess?: (hash: string) => void;
}) {
  const { address } = useAccount();

  const idsBigInt = requestIds.map(BigInt);

  const encodedData =
    address && idsBigInt.length
      ? encodeFunctionData({
          abi: StakingABI,
          functionName: 'redeem',
          args: [idsBigInt, address],
        })
      : undefined;

  // Use explicit gas limit for multiclaim (recommended by Monad docs)
  // Formula: 250_000 base + 50_000 per claim
  const explicitGasLimit = 250_000n + BigInt(requestIds.length) * 50_000n;

  const {
    transactionCost,
    gasLimit,
    originalGasEstimate,
    isLoadingTransactionCost,
  } = useEstimateGas({
    encodedData,
    toAddress: CONTRACT_ADDRESSES.staking,
    claimCount: requestIds.length,
    explicitGasLimit,
  });

  const { data: config, error: simulateError } = useSimulateContract({
    abi: StakingABI,
    address: CONTRACT_ADDRESSES.staking,
    functionName: 'redeem',
    args: [idsBigInt, address!],
    gas: gasLimit,
    query: {
      enabled: !!address && !!requestIds.length,
    },
  });

  const {
    data: hash,
    isPending: isWriting,
    error: writeError,
    writeContract,
  } = useWriteContract();

  const confirmationEnabled = !!hash;

  const {
    isLoading: isConfirming,
    isSuccess,
    error: confirmError,
  } = useWaitForTransactionReceipt({
    hash,
    query: { enabled: confirmationEnabled },
  });

  const { redeemedAmount, isLoadingRedeemedAmount, redeemedAmountError } =
    useRedeemedAmount(isSuccess ? hash : undefined);

  useEffect(() => {
    if (isSuccess && redeemedAmount) {
      notifications.show({
        title: 'Claim completed',
        message: (
          <div>
            <div>
              You successfully received{' '}
              {formatDecimal(redeemedAmount, {
                roundingMode: BigNumber.ROUND_FLOOR,
                withTruncate: true,
              })}{' '}
              MON
            </div>
            {hash && <TransactionLink hash={hash} />}
          </div>
        ),
        id: hash,
      });
      onSuccess?.(hash!);
      console.log('Claim completed with config:', config);
      trackSCWriteEvent('claim_completed', config, originalGasEstimate);
    }
  }, [isSuccess, redeemedAmount]);

  return {
    transactionCost: transactionCost.div(1e18),
    isLoadingTransactionCost,
    isRedeeming: isWriting || isConfirming || isLoadingRedeemedAmount,
    redeemError:
      simulateError ||
      writeError ||
      redeemedAmountError ||
      (confirmationEnabled ? confirmError : undefined),
    redeem: config?.request ? () => writeContract(config.request) : undefined,
  };
}
